import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import eventBus from '../EventBus';
import EPICYCLES3D from '../loaderEpicycles3d';
import { reveal } from '../../Reveal';
import { soundtrack } from '../../Audio/Soundtrack';
import SoundSwitch, { useSound } from './SoundSwitch';
import { IDLE_FOCUS, IDLE_FOV, IDLE_START_MS, idleCameraPosition, idleClock } from '../../Camera/IdlePath';

// Loading screen: the screen site's paper, with 3D Fourier ellipses drawing the desk and the chair in the room's own
// coordinates, seen through the site's own idle camera (Camera/IdlePath.ts): the slow sweep through the room starts
// with the loader, and when the paper fades the lines sit on the real desk and chair while the same sweep carries on.

const INK = 0x23302e;
const GHOST = 0x5d6b68;
const ORANGE = 0xec7a3c;
const DRAW_MS = 9000;           // drawing both parts; the paper fades once this is done and everything has loaded
const SHOWN = 40;               // ellipses drawn (the largest); the rest only move the arm
const SEG = 40;                 // vertices per drawn ellipse
// Close-up at the start: the camera sits at CLOSE of the idle distance, aimed at the drawing, and dollies back out to
// the idle path while the lines are drawn. Only the distance and the aim change; the sweep itself runs at its own pace.
const CLOSE = 0.65;
const CLOSE_HOLD = 0.2;         // part of DRAW_MS spent fully close before pulling out
// Hand-over to the room once the drawing is done and everything has loaded: the real desk, the chair and the small
// things on the desk fade in on the paper while the drawing dissolves, then everything else washes in around them.
const DESK_MS = 1600;
const DESK_HOLD = 400;          // a moment with only the desk before the room washes in
const ROOM_MS = 3400;
const LINE_MS = 240;            // the terminal prints a line about this often...
const LINE_MIN_MS = 45;         // ...and no faster than this when it has catching up to do
const smooth = (t: number) => { const k = Math.min(1, Math.max(0, t)); return k * k * (3 - 2 * k); };

type Part = { centre: number[]; ell: number[][]; pen: string };
const N = EPICYCLES3D.n;

function detectWebGL() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!(gl && gl instanceof WebGLRenderingContext);
}

// tip of the chain at sample s; also fills the joints (where each of the first SHOWN ellipses is centred)
function evaluate(part: Part, s: number, out: Float64Array, joints?: Float32Array) {
    const t = (2 * Math.PI * s) / N;
    let x = part.centre[0], y = part.centre[1], z = part.centre[2];
    for (let i = 0; i < part.ell.length; i++) {
        const e = part.ell[i];
        if (joints && i < SHOWN) { joints[i * 3] = x; joints[i * 3 + 1] = y; joints[i * 3 + 2] = z; }
        const c = Math.cos(e[0] * t), sn = Math.sin(e[0] * t);
        x += e[1] * c + e[4] * sn; y += e[2] * c + e[5] * sn; z += e[3] * c + e[6] * sn;
    }
    out[0] = x; out[1] = y; out[2] = z;
}

// every sample of the path at once: per ellipse, step its angle with a rotation instead of calling cos/sin each time
function trace(part: Part) {
    const X = new Float64Array(N).fill(part.centre[0]), Y = new Float64Array(N).fill(part.centre[1]), Z = new Float64Array(N).fill(part.centre[2]);
    for (const e of part.ell) {
        const d = (2 * Math.PI * e[0]) / N, cd = Math.cos(d), sd = Math.sin(d);
        let c = 1, sn = 0;
        for (let s = 0; s < N; s++) {
            X[s] += e[1] * c + e[4] * sn; Y[s] += e[2] * c + e[5] * sn; Z[s] += e[3] * c + e[6] * sn;
            const c2 = c * cd - sn * sd; sn = sn * cd + c * sd; c = c2;
        }
    }
    return [X, Y, Z];
}

type TermLine = { t: string; tag: string; text: string; right?: string; tone?: 'ok' | 'warn' };

// progress bar drawn with box characters, like a terminal would
const Bar: React.FC<{ pct: number }> = ({ pct }) => {
    const n = 18, on = Math.round(Math.min(1, Math.max(0, pct)) * n);
    return <span className="lt-bar"><b>{'━'.repeat(on)}</b>{'─'.repeat(n - on)}</span>;
};

const LoadingScreen: React.FC = () => {
    const holderRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef(0);
    // the side terminal: a log of what happens behind the paper, plus one live line for whatever is running now
    const t0 = useRef(performance.now());
    const counts = useRef({ loaded: 0, toLoad: 0 });
    const [lines, setLines] = useState<TermLine[]>([]);
    const [live, setLive] = useState<{ tag: string; text: string; pct?: number } | null>(null);
    // lines wait in a queue and print one at a time, so even a fast load reads like a terminal at work
    const queue = useRef<Omit<TermLine, 't'>[]>([]);
    const push = useCallback((tag: string, text: string, right?: string, tone?: TermLine['tone']) => {
        queue.current.push({ tag, text, right, tone });
    }, []);
    useEffect(() => {
        let timer = 0;
        const next = () => {
            const line = queue.current.shift();
            if (line) {
                const t = ((performance.now() - t0.current) / 1000).toFixed(2).padStart(5, '0');
                setLines((prev) => [...prev, { t, ...line }].slice(-40));
            }
            // ~6 lines a second with a little jitter, quicker when a backlog builds up
            const wait = Math.max(LINE_MIN_MS, LINE_MS - queue.current.length * 12) * (0.6 + Math.random() * 0.8);
            timer = window.setTimeout(next, wait);
        };
        timer = window.setTimeout(next, 300);
        return () => clearTimeout(timer);
    }, []);
    const [phase, setPhase] = useState<'draw' | 'wait' | 'reveal' | 'leave'>('draw');
    const [gone, setGone] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mobile = window.innerWidth < 768;
    const { running: soundOn } = useSound();

    const finish = useCallback(() => {
        Object.assign(reveal, { desk: 1, background: 1 });
        document.body.classList.remove('loader-paper');
        const ui = document.getElementById('ui');
        if (ui) ui.style.pointerEvents = 'none';
        eventBus.dispatch('loadingScreenDone', {});
    }, []);

    useEffect(() => {
        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1)
            setError('Because of a Firefox bug, the room does not work in Firefox yet. Please open it in another browser.');
        else if (!detectWebGL()) setError('This site needs WebGL. Please enable it or switch to a browser that supports it.');
        if (new URLSearchParams(window.location.search).has('debug')) {
            setGone(true); finish(); soundtrack.loop('server'); soundtrack.loop('waves'); return;
        }
        // the room starts empty, on the same paper as the loader (behind the 3D, so objects can appear on it)
        Object.assign(reveal, { desk: 0, background: 0 });
        document.body.classList.add('loader-paper');
        push('boot', 'jg-os room loader');
        push('gl', detectWebGL() ? 'webgl context ready' : 'no webgl context', undefined, detectWebGL() ? undefined : 'warn');
        // sound: the submarine loops from now; the rest is fetched ahead so each plays on cue
        (['startup', 'hum', 'server', 'waves'] as const).forEach((t) => soundtrack.load(t));
        soundtrack.loop('submarine', 1.5);
        push('audio', soundtrack.running ? 'submarine loop' : 'waiting for a click or key to start sound');
    }, []);

    // sound allowed (first click or key): say so in the terminal
    const soundWasOn = useRef(soundtrack.running);
    useEffect(() => {
        if (soundOn && !soundWasOn.current) push('audio', 'sound on · submarine loop', undefined, 'ok');
        soundWasOn.current = soundOn;
    }, [soundOn]);

    useEffect(() => eventBus.on('loadedSource', (data) => {
        progressRef.current = data.progress;
        counts.current = { loaded: data.loaded, toLoad: data.toLoad };
        if (data.loaded === 1) push('net', `fetching ${data.toLoad} resources`);
        push('load', data.sourceName, `${Math.round(data.progress * 100)}%`);
        if (data.progress >= 1) push('ok', 'all resources loaded', undefined, 'ok');
    }), []);

    useEffect(() => {
        const holder = holderRef.current;
        if (!holder || !detectWebGL()) return;
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setClearColor(0x000000, 0);
        holder.appendChild(renderer.domElement);
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(35, 1, 10, 900000);
        const parts: Part[] = [EPICYCLES3D.parts.desk, EPICYCLES3D.parts.chair];
        const tip = new Float64Array(3);

        // the ink: every pen-down step of the finished path, revealed in order as the pen passes
        const inkMat = new LineMaterial({ color: INK, linewidth: 1.5, transparent: true });
        const bounds = new THREE.Box3(), point = new THREE.Vector3();
        const inks = parts.map((part) => {
            const [X, Y, Z] = trace(part);
            const pos: number[] = [];
            const upTo = new Int32Array(N + 1);         // segments to show once the pen has reached sample s
            for (let s = 0; s < N; s++) {
                const b = (s + 1) % N;
                if (part.pen[s] === '1' && part.pen[b] === '1') {
                    pos.push(X[s], Y[s], Z[s], X[b], Y[b], Z[b]);
                    bounds.expandByPoint(point.set(X[s], Y[s], Z[s]));
                }
                upTo[s + 1] = pos.length / 6;
            }
            const geo = new LineSegmentsGeometry(); geo.setPositions(pos); geo.instanceCount = 0;
            const line = new LineSegments2(geo, inkMat); line.frustumCulled = false; scene.add(line);
            return { geo, upTo };
        });

        // the mechanism: the largest ellipses, the arm through their joints, and the pen
        const ghostMat = new THREE.LineBasicMaterial({ color: GHOST, transparent: true, opacity: 0.32 });
        const armMat = new THREE.LineBasicMaterial({ color: INK, transparent: true, opacity: 0.5 });
        const rigs = parts.map((part) => {
            const shown = Math.min(SHOWN, part.ell.length);
            const ellGeo = new THREE.BufferGeometry();
            ellGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(shown * SEG * 2 * 3), 3));
            const ellipses = new THREE.LineSegments(ellGeo, ghostMat); ellipses.frustumCulled = false;
            const armGeo = new THREE.BufferGeometry();
            armGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array((shown + 1) * 3), 3));
            const arm = new THREE.Line(armGeo, armMat); arm.frustumCulled = false;
            const pen = new THREE.Mesh(new THREE.SphereGeometry(28, 12, 8), new THREE.MeshBasicMaterial({ color: ORANGE }));
            const group = new THREE.Group(); group.add(ellipses, arm, pen); scene.add(group);
            return { part, shown, ellGeo, armGeo, pen, group, joints: new Float32Array(SHOWN * 3) };
        });
        const updateRig = (r: typeof rigs[number], s: number) => {
            evaluate(r.part, s, tip, r.joints);
            const ep = r.ellGeo.attributes.position.array as Float32Array;
            for (let i = 0; i < r.shown; i++) {                 // harmonic i sweeps the ellipse A cos + B sin around its joint
                const e = r.part.ell[i], jx = r.joints[i * 3], jy = r.joints[i * 3 + 1], jz = r.joints[i * 3 + 2];
                for (let v = 0; v < SEG; v++) {
                    for (let k = 0; k < 2; k++) {
                        const th = (2 * Math.PI * (v + k)) / SEG, c = Math.cos(th), sn = Math.sin(th);
                        const o = ((i * SEG + v) * 2 + k) * 3;
                        ep[o] = jx + e[1] * c + e[4] * sn; ep[o + 1] = jy + e[2] * c + e[5] * sn; ep[o + 2] = jz + e[3] * c + e[6] * sn;
                    }
                }
            }
            r.ellGeo.attributes.position.needsUpdate = true;
            const ap = r.armGeo.attributes.position.array as Float32Array;
            ap.set(r.joints.subarray(0, r.shown * 3)); ap[r.shown * 3] = tip[0]; ap[r.shown * 3 + 1] = tip[1]; ap[r.shown * 3 + 2] = tip[2];
            r.armGeo.attributes.position.needsUpdate = true;
            r.pen.position.set(tip[0], tip[1], tip[2]);
        };

        const resize = () => {
            const w = window.innerWidth, h = window.innerHeight;
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2)); renderer.setSize(w, h);
            camera.aspect = w / h; camera.fov = IDLE_FOV; camera.updateProjectionMatrix();
            inkMat.resolution.set(w, h);
        };
        resize();
        window.addEventListener('resize', resize);

        parts.forEach((p, i) => push('fourier', `${i ? 'chair' : 'desk'} · ${p.ell.length} epicycles · ${N} samples`));
        const subject = bounds.getCenter(new THREE.Vector3());     // the close-up aims at the middle of the drawing
        const aim = new THREE.Vector3();
        const start = performance.now();
        let stage: 'draw' | 'wait' | 'desk' | 'room' | 'done' = 'draw', raf = 0, since = 0;
        let liveAt = 0, saidOutside = false, saidCamera = false;
        const say = (now: number, l: { tag: string; text: string; pct?: number } | null) => {      // live line, ~8 times a second
            if (l && now - liveAt < 120) return;
            liveAt = now; setLive(l);
        };
        const frame = (now: number) => {
            idleClock.ms = IDLE_START_MS + now - start;     // the idle sweep runs from the first frame
            if (stage === 'draw') {
                const f = Math.min(1, (now - start) / DRAW_MS);
                const s = f * N;
                inks.forEach((ink) => { ink.geo.instanceCount = ink.upTo[Math.min(N, Math.floor(s))]; });
                rigs.forEach((r) => updateRig(r, Math.min(s, N - 1e-3)));
                say(now, { tag: 'trace', text: 'desk + chair', pct: f });
                if (f >= 1) {
                    rigs.forEach((r) => (r.group.visible = false));
                    inks.forEach((ink) => { ink.geo.instanceCount = ink.upTo[N]; });
                    stage = 'wait'; setPhase('wait');
                    say(now, null); push('trace', 'drawing complete', undefined, 'ok');
                    if (soundtrack.running) push('audio', 'console start-up');
                    soundtrack.once('startup');
                    soundtrack.stop('submarine', 2.5);
                }
            } else if (stage === 'wait' && !(progressRef.current >= 1 && reveal.ready)) {
                const { loaded: l, toLoad: n } = counts.current;
                say(now, { tag: 'wait', text: `resources ${l}/${n || '-'}`, pct: n ? l / n : 0 });
            } else if (stage === 'wait') {
                stage = 'desk'; since = now; setPhase('reveal');
                say(now, null);
                push('scene', 'desk, chair and what sits on it');
                push('ink', 'dissolving the drawing');
            } else if (stage === 'desk') {
                const d = smooth((now - since) / DESK_MS);
                reveal.desk = d; inkMat.opacity = 1 - d;
                if (now - since >= DESK_MS + DESK_HOLD) {
                    stage = 'room'; since = now; setPhase('leave');
                    push('scene', 'washing in the room from the desk');
                    soundtrack.once('hum');
                    soundtrack.loop('server', ROOM_MS / 1000);
                    soundtrack.loop('waves', ROOM_MS / 1000);
                    push('audio', 'console hum · server + lake waves');
                }
            } else if (stage === 'room') {
                reveal.background = Math.min(1, (now - since) / ROOM_MS);      // steady: the shader shapes the edge
                if (!saidOutside && reveal.background > 0.55) { saidOutside = true; push('scene', 'sea and sky'); }
                if (!saidCamera && reveal.background > 0.8) { saidCamera = true; push('cam', 'handing over to the idle orbit', undefined, 'ok'); }
                if (reveal.background >= 1) { stage = 'done'; setGone(true); finish(); }
            }
            // close-up easing back to the idle camera by the end of the drawing (smoothstep: no jolt at either end)
            const k = Math.min(1, Math.max(0, ((now - start) / DRAW_MS - CLOSE_HOLD) / (1 - CLOSE_HOLD)));
            const e = k * k * (3 - 2 * k);
            aim.copy(subject).lerp(IDLE_FOCUS, e);
            idleCameraPosition(idleClock.ms, camera.position).sub(aim).multiplyScalar(CLOSE + (1 - CLOSE) * e).add(aim);
            camera.lookAt(aim);
            renderer.render(scene, camera);
            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf); window.removeEventListener('resize', resize);
            scene.traverse((o: any) => { o.geometry?.dispose?.(); o.material?.dispose?.(); });
            renderer.dispose(); renderer.domElement.remove();
        };
    }, []);

    if (gone) return null;
    return (
        <div className={'loader' + (phase === 'reveal' ? ' is-revealing' : phase === 'leave' ? ' is-revealing is-leaving' : '')}>
            <div ref={holderRef} className="loader-canvas" />
            <div className="loader-brand">
                <div>
                    <div className="loader-name">JAVIER GARCÍA</div>
                    <div className="loader-role">GAME DEVELOPER</div>
                    <div className="loader-sound">
                        <SoundSwitch tone="ink" />
                    </div>
                </div>
            </div>
            <div className="loader-term" aria-hidden="true">
                {lines.map((l, i) => (
                    <div key={i} className={'lt-line' + (l.tone ? ' is-' + l.tone : '')}>
                        <span className="lt-t">{l.t}</span><span className="lt-tag">{l.tag}</span>
                        <span className="lt-text">{l.text}</span><span className="lt-right">{l.right}</span>
                    </div>
                ))}
                {live && (
                    <div className="lt-line is-live">
                        <span className="lt-t" /><span className="lt-tag">{live.tag}</span>
                        <span className="lt-text">{live.text}{live.pct !== undefined && <Bar pct={live.pct} />}</span>
                        <span className="lt-right">{live.pct !== undefined ? `${Math.floor(live.pct * 100)}%` : ''}</span>
                    </div>
                )}
                <div className="lt-line lt-prompt"><span className="lt-t" /><span className="lt-tag">$</span><span className="lt-text">
                    {/* until the browser allows sound, the prompt asks for the first click or key */}
                    {!soundOn && <span className="lt-hint">{mobile ? 'tap for sound' : 'press any key or click for sound'}</span>}
                    <span className="loader-cursor" /></span></div>
            </div>
            {mobile && <div className="loader-note">BEST ON A DESKTOP</div>}
            {error && (
                <div className="loader-error">
                    <b>CAN'T OPEN THE ROOM</b>
                    <p>{error}</p>
                </div>
            )}
        </div>
    );
};

export default LoadingScreen;
